import { produce } from 'immer';
import pickBy from 'lodash/pickBy';
import compact from 'lodash/compact';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import union from 'lodash/union';
import each from 'lodash/each';
import keys from 'lodash/keys';
import map from 'lodash/map';
import noop from 'lodash/noop';

import {
  showErrorNotification
} from './misc.js';

import {
  sendRequest
} from 'zoomforth_zfutils/xhr_tools.js';

import {
  startsWithVowel
} from 'zoomforth_zfutils/character_tools.js';

import {
  getContentPayloadForReplace
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  getIdFrom,
  getUuidFrom
} from 'zoomforth_codesbane_viewer/lib/id_utils.js';

import {
  getDisplayableAssetType,
  shouldUseNameAndCaptionForDefaults
} from 'zoomforth_codesbane_editor/lib/assets.js';

import {
  getEditorTileGridPositionByEid
} from 'zoomforth_codesbane_editor/lib/section_overrides/grid_section_overrides.js';

import {
  getContentUuidByEid
} from 'zoomforth_codesbane_editor/lib/section_overrides/ordered_content_section_overrides.js';

import { 
  getPageId,
  getPendingSave,
  getSendingOrBlockingSave,
  getEditorOnlySectionOverrides
} from 'zoomforth_codesbane_editor/reducers/index.js';

import { 
  getSectionById,
  getAssetForContent,
  getContentItemByUuid,
  getSectionBackgroundData,
  getSecondaryLogoData,
  getTranslationsCurrentLanguageIso,
  getTranslationsDefaultLanguageIso
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  isSingletonContentSection,
  isOrderedContentSection,
  isGridPositionsContentSection
} from 'zoomforth_codesbane_viewer/lib/sections.js';

import {
  toStandardPageContentApiModel,
  toStructuredStandardPageContentItem
} from 'zoomforth_codesbane_viewer/transformers/transform_standard_page_content_item.js';

import {
  ensureThrottledPageConfigSaving
} from './config.js';

import {
  makeSaveId,
  unblockSave,
  startSave,
  pendSave,
  sendSave,
  commitSave,
  failSave
} from 'zoomforth_shared_management/action_creators/saving.js';

import {
  ensureThrottledTranslationUpdate
} from './translations.js';

import {
  notifyErrorMaybeLostData
} from './misc.js';

import {
  setSectionAndOverrides,
  setSectionBackgroundData,
  setSectionFallbackBackgroundData,
  setSectionSimpleData
} from './sections.js';

import {
  updateAssetData
} from './asset.js';

import {
  setSecondaryLogoData
} from './header.js';

import {
  markAsDraft
} from './versioning.js';

import {
  updateOrderedContentSectionWithNewContent,
  addNewContentItemToOrderedContentSection
} from './sections.ordered_content.js';

import {
  updateSingletonContentSectionWithNewContent
} from './sections.singleton.js';

import {
  updateGridPositionsContentSectionWithNewContent
} from './sections.grid.js';

import { 
  CONTENT_TYPE_ASSET,
  CONTENT_TYPE_API_FEED,
  CONTENT_TYPE_IFRAME_EMBED,
  CONTENT_TYPE_TEXT,
  CONTENT_TYPE_OVERLAY,
  CONTENT_TYPE_DOWNLOADABLE,
  CONTENT_TYPE_SECTION_BACKGROUND,
  CONTENT_TYPE_ACCORDION_ITEM,
  CONTENT_TYPE_TAB_ITEM
} from 'zoomforth_codesbane_viewer/const/content_types.js';

const CONTENT_ITEM_SAVE_THROTTLE_DELAY = 2000; // Milliseconds between saves

export const createContentItem = (section, data, positionData, {isForReplace=false, shouldEditOnCreate=true, canShowUserCropModal=true, shouldScrollToOnCreate=false}={})=>
  (dispatch, getState)=> {
    const state = getState();

    if (isForReplace) {
      const { eid } = positionData;
      const sectionOverrides = getEditorOnlySectionOverrides(getState(), section.id);

      var uuid;

      if (isGridPositionsContentSection(section)) {
        const gridPosition = getEditorTileGridPositionByEid(section, sectionOverrides, eid);

        uuid = gridPosition.uuid;

      } else if (isOrderedContentSection(section)) {
        uuid = getContentUuidByEid(section, sectionOverrides, eid);

      }

      const contentItem = getContentItemByUuid(getState(), uuid);

      data = { ...getContentPayloadForReplace(contentItem), ...data };
    }

    const saveData = toStandardPageContentApiModel({
      lens_id: getPageId(state),
      ...data
    });

    const defaultLanguageIso = getTranslationsDefaultLanguageIso(state);
    const currentLanguageIso = getTranslationsCurrentLanguageIso(state);
    const isEditingSecondaryLang = currentLanguageIso !== defaultLanguageIso;

    const saveId = makeSaveId();
    dispatch(startSave('create_content_item', saveId, 'Create content item.'));
    dispatch(markAsDraft());

    sendRequest({
      url: '/api/page_content_items',
      method: 'POST',
      params: {
        ...saveData,
        'is_editing_secondary_language': isEditingSecondaryLang
      },

      onSuccess: (receivedData)=> {
        // Do not mark save status as saved because we're not really
        // in a consistent state until the config is saved after receiving this content!
        dispatch(receiveNewContentItem(
          section, 
          toStructuredStandardPageContentItem(receivedData), 
          positionData, 
          {shouldEditOnCreate, canShowUserCropModal, shouldScrollToOnCreate}
        ));
        dispatch(commitSave(saveId));
      },

      onError: ({ status, responseText })=> {
        dispatch(failSave(saveId));
        dispatch(notifyErrorMaybeLostData(status, responseText));
      }
    });

  };


/**
 * For content that simply needs to be registered in the site we can use this
 * crate function. This is primarily to create overlays which can exist in any
 * section at any time.
 */
export const createSectionlessContentItem = (data, onReceiveContentItem=noop)=>
  (dispatch, getState)=> {
    const state = getState();

    const saveData = toStandardPageContentApiModel({
      ...data,
      lens_id: getPageId(state)
    });

    const saveId = makeSaveId();

    dispatch(startSave('create_content_item', saveId, 'Create content item.'));
    dispatch(markAsDraft());

    sendRequest({
      url: '/api/page_content_items',
      method: 'POST',
      params: saveData,

      onSuccess: (receivedData)=> {
        const contentItem = toStructuredStandardPageContentItem(receivedData);

        dispatch({ type: 'ADD_PAGE_CONTENT_ITEM', contentItem });

        onReceiveContentItem(contentItem);

        dispatch(commitSave(saveId));
      },

      onError: ({ status, responseText })=> {
        dispatch(failSave(saveId));
        dispatch(notifyErrorMaybeLostData(status, responseText));
      }
    });

  };

const destroyContentItem = (contentItem)=>
  (dispatch)=> {

    if (!contentItem || !contentItem.id) {
      throw new Error('Cannot delete ContentItem; missing id');
    }

    const saveId = makeSaveId();
    dispatch(startSave('destroy_content_item', saveId, 'Destroy content item'));
    dispatch(markAsDraft());

    sendRequest({
      url: `/api/page_content_items/${contentItem.id}`,
      method: 'DELETE',
      onSuccess: ()=> {
        dispatch(commitSave(saveId));
      },
      onError: ({ status, responseText })=> {
        dispatch(failSave(saveId));
        dispatch(notifyErrorMaybeLostData(status, responseText));
      }
    });

  };



export const receiveNewContentItem = (section, contentItem, positionData={}, {shouldEditOnCreate, canShowUserCropModal, shouldScrollToOnCreate})=>
  (dispatch)=> {

    dispatch({
      type: 'ADD_PAGE_CONTENT_ITEM',
      contentItem
    });

    if (isGridPositionsContentSection(section)) {
      dispatch(updateGridPositionsContentSectionWithNewContent(section, contentItem, positionData, {shouldEditOnCreate, canShowUserCropModal, shouldScrollToOnCreate}));

    } else if (isOrderedContentSection(section)) {
      dispatch(updateOrderedContentSectionWithNewContent(section, contentItem, positionData, {shouldEditOnCreate}));

    } else if (isSingletonContentSection(section)) {
      dispatch(updateSingletonContentSectionWithNewContent(section, contentItem, positionData));

    } else {
      throw new Error('Cannot receive content for section with unexpected content ownership definition');
    }

  };


export const createTextContentItem = (section, positionData, extraData={})=>
  (dispatch)=> {
    dispatch(createContentItem(section, {
      ...extraData,
      content_type: CONTENT_TYPE_TEXT 
    }, positionData
    )); 
  };


export const createJobFeedContentItem = (section, positionData)=>
  (dispatch)=> {
    dispatch(createContentItem(
      section, 
      { 
        content_type: CONTENT_TYPE_API_FEED
      }, 
      positionData
    )); 
  };

const DEFAULT_ACCORDION_ITEM_COUNT = 3;
export const createAccordionContentItems = (section, positionData) =>
  (dispatch) => {
    for(let i = 0; i < DEFAULT_ACCORDION_ITEM_COUNT; i++){
      dispatch(addNewContentItemToOrderedContentSection(
        section, 
        {
          content_type: CONTENT_TYPE_ACCORDION_ITEM,
          item: { label: null, content: null }
        }
      ));
    }
  };

const DEFAULT_TAB_COUNT = 0;
export const createTabsContentItems = (section, positionData) =>
  (dispatch, getState) => {
    
    const tabs = [];
    
    for(let i = 0; i < DEFAULT_TAB_COUNT; i++){
      const newTab = {
        id: `tab${Date.now() + i}`,
        title: `Tab ${i + 1}`,
        content: {section: null},
      };
      tabs.push(newTab);
    }
    
    const currentSection = getSectionById(getState(), section.id);
    const dataToSet = { tabs };

    if (!currentSection || !currentSection.innerBackgroundData) {
      dataToSet.innerBackgroundData = {
        backgroundType: 'color',
        backgroundColor: '#EEEEEE'
      };
    }

    dispatch(setSectionSimpleData(section.id, dataToSet));
  };


export const createIframeEmbedContentItem = (section, iframeEmbedUrl, positionData={})=>
  (dispatch)=> {
    dispatch(createContentItem(
      section,
      {
        content_type: CONTENT_TYPE_IFRAME_EMBED,
        iframe_embed_url: iframeEmbedUrl,
        iframe_title: '',
        tile_action_data: { type: 'none' }
      },
      positionData
    ));
  };


export const createContentItemFromAsset = (section, asset, positionData={}, extraContentData={}, {shouldEditOnCreate, canShowUserCropModal, shouldScrollToOnCreate, isForReplace}={})=>
  (dispatch)=> {

    if (shouldUseNameAndCaptionForDefaults(asset)) {
      extraContentData.title = extraContentData.title || asset.name;
      extraContentData.caption = extraContentData.caption || asset.caption;
      extraContentData.renderedCaption = extraContentData.renderedCaption || asset.renderedCaption;
    }

    dispatch(updateAssetData(asset));

    dispatch(createContentItem(section, {
      content_type: CONTENT_TYPE_ASSET,
      asset_type: asset.assetType,
      asset_id: asset.assetId,
      ...extraContentData

    }, positionData, {isForReplace, shouldEditOnCreate, canShowUserCropModal, shouldScrollToOnCreate}));
  };


export const createOverlayContentItemFromAsset = (asset, extraData={}, onReceiveContentItem=noop)=>
  (dispatch)=> {
    dispatch(updateAssetData(asset));

    dispatch(createSectionlessContentItem({ 
      ...extraData,
      content_type: CONTENT_TYPE_OVERLAY,
      asset_type: asset.assetType,
      asset_id: asset.assetId
    }, onReceiveContentItem));
  };


export const createDownloadableContentItemFromAsset = (asset, onReceiveContentItem=noop)=>
  (dispatch)=> {
    dispatch(updateAssetData(asset));

    dispatch(createSectionlessContentItem({ 
      content_type: CONTENT_TYPE_DOWNLOADABLE,
      asset_type: asset.assetType,
      asset_id: asset.assetId
    }, onReceiveContentItem));
  };


export const deleteContentItems = (contentItemsOrUuids)=>
  (dispatch, getState)=> {
    const contentUuids = map(contentItemsOrUuids, getUuidFrom);
    const contentItems = compact(map(contentUuids, uuid => getContentItemByUuid(getState(), uuid)));

    dispatch({
      type: 'DELETE_CONTENT_ITEMS',
      contentUuids
    });

    dispatch(ensureThrottledPageConfigSaving());

    each(contentItems, contentItem => {
      dispatch(destroyContentItem(contentItem));
    });

  };



export const saveContentItem = (contentItemOrUuid)=> 
  (dispatch, getState)=> {
    const state = getState();
    const uuid = getUuidFrom(contentItemOrUuid);

    const pageContentItem = getContentItemByUuid(state, uuid);

    const pendingSaveAttrData = getPendingSave(getState(), `content_item_update-${uuid}`);
    // Merge all the attrs from all the pending saves for this uuid
    const attrList = union.apply(null, map(pendingSaveAttrData, 'attrs'));

    const pageContentItemData = pickBy(pageContentItem, (v,i) => includes(attrList, i));
    const saveableContentData = toStandardPageContentApiModel(pageContentItemData);

    const defaultLanguageIso = getTranslationsDefaultLanguageIso(state);
    const currentLanguageIso = getTranslationsCurrentLanguageIso(state);
    const isEditingSecondaryLang = currentLanguageIso !== defaultLanguageIso;

    if (isEditingSecondaryLang) {
      dispatch(ensureThrottledTranslationUpdate());
    } else {
      const saveId = makeSaveId();
      dispatch(sendSave(`content_item_update-${uuid}`, saveId));
      dispatch(markAsDraft());

      sendRequest({

        url: `/api/page_content_items/${pageContentItem.id}`,
        method: 'PATCH',
        params: {
          ...saveableContentData,
          'is_editing_secondary_language': isEditingSecondaryLang
        },

        onSuccess: (receivedData)=> {

          dispatch(commitSave(saveId, { blockNext:true })); // We successfully saved

          setTimeout(()=> {
            if (getPendingSave(getState(), `content_item_update-${uuid}`)) {
              dispatch(saveContentItem(uuid)); // Also unblocks
            } else {
              dispatch(unblockSave(`content_item_update-${uuid}`));
            }

          }, CONTENT_ITEM_SAVE_THROTTLE_DELAY);

          if (!getPendingSave(getState(), `content_item_update-${uuid}`)) {
            // Sometimes the python app modifies the saved data, as such,
            // we want to merge the response data back into state, to capture
            // these potential changes. But, we can only do so if there
            // are no pending changes. If there are pending changes, we can
            // safely ignore this response, because the currently-pending save
            // will generate a new response.
            dispatch({
              type: 'SET_CONTENT_ITEM_SIMPLE_DATA',
              contentUuid: uuid,
              data: toStructuredStandardPageContentItem(receivedData)
            });

            dispatch(ensureThrottledTranslationUpdate());
          }

        },

        onError: ({ status, responseText })=> {
          dispatch(failSave(saveId));
          dispatch(notifyErrorMaybeLostData(status, responseText));
        }
      });
    }

  };


export const setContentItemAsset = (contentItemOrUuid, asset)=>
  (dispatch, getState)=> {
    const contentUuid = getUuidFrom(contentItemOrUuid);
    const contentItem = getContentItemByUuid(getState(), contentUuid);
    const oldAsset = getAssetForContent(getState(), contentItem);

    if (oldAsset) {
      if (oldAsset.assetType !== asset.assetType) {
        // make a vowel-aware string for better UX
        const makeAssetStrn = (asset)=> {
          const displayableAssetType = getDisplayableAssetType(asset);
          if (startsWithVowel(displayableAssetType)) {
            return `an ${displayableAssetType}`;
          }
          return `a ${displayableAssetType}`;
        };

        dispatch(showErrorNotification(
          'Asset error',
          `You cannot replace ${makeAssetStrn(oldAsset)} asset with
          ${makeAssetStrn(asset)} asset. Please try again using
          ${makeAssetStrn(oldAsset)} asset.`
        ));
        return;
      }
    } 

    dispatch({
      type: 'SET_CONTENT_ITEM_ASSET',
      contentUuid,
      asset
    });

    dispatch(ensureThrottledContentItemSaving(contentUuid, ['asset_id', 'asset_type']));

  };


export const setContentItemSimpleData = (contentItemOrUuid, data)=> (dispatch, getState)=> {
  const contentUuid = getUuidFrom(contentItemOrUuid);
  const contentItem = getContentItemByUuid(getState(), contentUuid);
  const contentItemAttrs = pickBy(contentItem, (v,i) => includes(keys(data), i));

  // If nothing changes then no need to process a save
  if (isEqual(contentItemAttrs, data)) {
    return;
  }
    
  dispatch({
    type: 'SET_CONTENT_ITEM_SIMPLE_DATA',
    contentUuid,
    data
  });

  dispatch(ensureThrottledContentItemSaving(contentUuid, keys(data)));
};

export const createSectionBackgroundContent = (sectionOrId, asset, onContentReceived)=>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);

    dispatch(updateAssetData(asset));
    dispatch(setSectionAndOverrides(sectionId, section, { isLoading: true }));

    const saveData = toStandardPageContentApiModel({
      lens_id: getPageId(getState()),
      content_type: CONTENT_TYPE_SECTION_BACKGROUND,
      asset_type: asset.assetType,
      asset_id: asset.assetId,
      tile_display_type: 'library'
    });

    const saveId = makeSaveId();
    dispatch(startSave('create_section_bg_content_item', saveId, 'Create section background content'));
    dispatch(markAsDraft());

    sendRequest({
      url: '/api/page_content_items',
      method: 'POST',
      params: saveData,
      onSuccess: (receivedData)=> {
        const newContentItem = toStructuredStandardPageContentItem(receivedData);

        dispatch({
          type: 'ADD_PAGE_CONTENT_ITEM',
          contentItem: newContentItem
        });

        onContentReceived(section, newContentItem);

        const updatedSection = getSectionById(getState(), sectionId);
        dispatch(setSectionAndOverrides(sectionId, updatedSection, { isLoading: false }));

        dispatch(commitSave(saveId));
      },

      onError: ({ status, responseText })=> {
        dispatch(failSave(saveId));
        dispatch(notifyErrorMaybeLostData(status, responseText));
      }
    });
  };


export const createSectionBackgroundImageContent = (sectionOrId, asset, isFallback = false, dataKey = 'backgroundData')=>
  (dispatch, getState)=> {
    dispatch(createSectionBackgroundContent(sectionOrId, asset, (section, newContentItem)=> {
      const currentBackground = getSectionBackgroundData(getState(), section, dataKey) || {};
      const {
        backgroundImageSize,
        backgroundImageRepeat,
        backgroundImagePosition,
        backgroundZfImage
      } = currentBackground;
      
      const setterFunc = isFallback ? setSectionFallbackBackgroundData : setSectionBackgroundData;

      dispatch(setterFunc(section, {
        ...currentBackground,
        backgroundType: 'image',
        backgroundImageContentUuid: newContentItem.uuid,
        backgroundImageSize: backgroundImageSize || 'cover',
        backgroundImageRepeat: backgroundImageRepeat || 'repeat',
        backgroundImagePosition: backgroundImagePosition || 'center',
        backgroundZfImage: backgroundZfImage,
        // default to using the uncompressed image as recommended by our designers
        isUsingUncompressedBackgroundImage: !asset.isVector && !!asset.originalImage
      }, dataKey));
    }));
  };

export const createSectionBackgroundVideoContent = (sectionOrId, asset, isFallback = false, dataKey = 'backgroundData')=>
  (dispatch, getState)=> {
    dispatch(createSectionBackgroundContent(sectionOrId, asset, (section, newContentItem)=> {
      const currentBackground = getSectionBackgroundData(getState(), section, dataKey) || {};
      const {
        backgroundVideoLoop,
        backgroundVideoObjectFit,
        backgroundVideoObjectPosition
      } = currentBackground;

      const setterFunc = isFallback ? setSectionFallbackBackgroundData : setSectionBackgroundData;

      dispatch(setterFunc(section, {
        ...currentBackground,
        backgroundType: 'video',
        backgroundVideoContentUuid: newContentItem.uuid,
        backgroundVideoLoop: backgroundVideoLoop || true,
        backgroundVideoObjectFit: backgroundVideoObjectFit || 'cover',
        backgroundVideoObjectPosition: backgroundVideoObjectPosition || 'center'
      }, dataKey));
    }));
  };

export const createSecondaryLogoContent = (asset)=>
  (dispatch, getState)=> {
    dispatch(updateAssetData(asset));

    const saveData = toStandardPageContentApiModel({
      lens_id: getPageId(getState()),
      content_type: CONTENT_TYPE_ASSET,
      asset_type: asset.assetType,
      asset_id: asset.assetId,
      tile_display_type: 'library'
    });

    const saveId = makeSaveId();
    dispatch(startSave('create_secondary_logo_content_item', saveId, 'Create secondary logo content'));
    dispatch(markAsDraft());

    sendRequest({
      url: '/api/page_content_items',
      method: 'POST',
      params: saveData,
      onSuccess: (receivedData)=> {
        const newContentItem = toStructuredStandardPageContentItem(receivedData);

        dispatch({
          type: 'ADD_PAGE_CONTENT_ITEM',
          contentItem: newContentItem
        });

        const { backgroundSize, backgroundPosition } = getSecondaryLogoData(getState());

        dispatch(setSecondaryLogoData({
          contentUuid: newContentItem.uuid,
          backgroundSize: backgroundSize || 'cover',
          backgroundPosition: backgroundPosition || 'center'
        }));

        dispatch(commitSave(saveId));
      },

      onError: ({ status, responseText })=> {
        dispatch(failSave(saveId));
        dispatch(notifyErrorMaybeLostData(status, responseText));
      }
    });
  };

export const ensureThrottledContentItemSaving = (uuid, attrs)=>
  (dispatch, getState)=> {
    const state = getState();
    const defaultLanguageIso = getTranslationsDefaultLanguageIso(state);
    const currentLanguageIso = getTranslationsCurrentLanguageIso(state);
    const isEditingSecondaryLang = currentLanguageIso !== defaultLanguageIso;

    dispatch(markAsDraft());

    // we don't want to create a pending save in a secondary language since it
    // will never complete
    if (!isEditingSecondaryLang) {
      dispatch(pendSave(`content_item_update-${uuid}`, `Content item save (uuid=${uuid})`, { attrs }));
    }

    if (!getSendingOrBlockingSave(getState(), `content_item_update-${uuid}`)) {
      dispatch(saveContentItem(uuid));
    }

  };

export const updateAnimations = (contentItem, produceFunction) => (dispatch) => {
  dispatch(setContentItemSimpleData(contentItem, { 
    animationSettings: produce(contentItem.animationSettings||{}, produceFunction)
  }));
};
  
